/**
 * /crm/tickets/:id — full ticket workspace.
 *
 * Two columns on large screens:
 *   - left:   order context, status/priority/note editor, assignments
 *   - right:  disposition, the type-specific editor (refund / shrinkage),
 *             then the case history with the composer docked underneath
 *
 * The type-specific editor sits above the history because it is the reason
 * the ticket exists; the QR/share card is last because it is a convenience,
 * not the job.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronRight,
  Link2,
  ShieldAlert,
  UserPlus,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { AssignmentDialog } from '@/components/crm/AssignmentDialog';
import { CallRecordingUpload } from '@/components/crm/CallRecordingUpload';
import { GuardedAction, TicketStateCard } from '@/components/crm/TicketStateCard';
import { InteractionTimeline } from '@/components/crm/InteractionTimeline';
import { RefundEditor } from '@/components/crm/RefundEditor';
import { ShrinkageEditor } from '@/components/crm/ShrinkageEditor';
import { TicketDispositionCard } from '@/components/crm/TicketDispositionCard';
import { TicketSummary } from '@/components/crm/TicketSummary';
import { useTicketCapabilities } from '@/features/crm/useTicketCapabilities';
import { useCrmTicket } from '@/api/bi/crm';
import { useOrderDetail } from '@/api/legacy/orders';
import {
  TICKET_PRIORITY_LABEL,
  TICKET_PRIORITY_TONE,
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
  TICKET_TYPE_LABEL,
  TICKET_TYPE_TONE,
} from '@/lib/crmStatus';
import { fmt } from '@/lib/format';
import { notify } from '@/lib/toast';
import { CycleDamageCard } from '@/components/crm/CycleDamageCard';
import {  CycleTicketResolveCard } from '@/components/crm/CycleTicketResolveCard';

export function CrmTicketDetailPage() {
  const params = useParams();
  const ticketId = params.id ? Number(params.id) : null;
  const ticketQuery = useCrmTicket(ticketId);
  const [assignOpen, setAssignOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);

  const ticket = ticketQuery.data;
  const capabilities = useTicketCapabilities(ticket);

  // The linked order carries the call recordings and remarks that staff and
  // support left on the order itself — merged into the ticket history below.
  const orderDetail = useOrderDetail(ticket?.order_id ?? null);
  const orderRemarks = useMemo(
    () =>
      orderDetail.data
        ? {
            packer: orderDetail.data.packer?.remarks ?? null,
            biller: orderDetail.data.biller?.remarks ?? null,
            rider: orderDetail.data.rider?.remarks ?? null,
            customer: orderDetail.data.user_info?.remarks ?? null,
          }
        : null,
    [orderDetail.data],
  );

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notify.success('Ticket link copied.');
    } catch {
      notify.error('Could not copy the link.');
    }
  }

  if (ticketId == null) return <ErrorState title="Missing ticket id" />;
  if (ticketQuery.isError)
    return <ErrorState onRetry={() => void ticketQuery.refetch()} />;

  return (
    <>
      <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Link
          to="/crm/tickets"
          className="press inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:text-foreground focus-ring"
        >
          <ArrowLeft className="h-3 w-3" /> Tickets
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span className="font-medium text-foreground">#{ticketId}</span>
      </div>

      <PageHeader
        breadcrumbs
        eyebrow="CRM Ticket"
        title={
          ticket
            ? `#${ticket.id} ${ticket.summary || TICKET_TYPE_LABEL[ticket.type]}`
            : `#${ticketId}`
        }
        description={
          ticket ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              <Badge tone={TICKET_TYPE_TONE[ticket.type]}>
                {TICKET_TYPE_LABEL[ticket.type]}
              </Badge>
              <Badge tone={TICKET_STATUS_TONE[ticket.status]}>
                {TICKET_STATUS_LABEL[ticket.status]}
              </Badge>
              <Badge tone={TICKET_PRIORITY_TONE[ticket.priority]}>
                {TICKET_PRIORITY_LABEL[ticket.priority]}
              </Badge>
              {ticket.is_auto_created ? <Badge tone="info">Auto</Badge> : null}
              <span className="text-xs text-muted-foreground">
                created {fmt.ago(ticket.created_at)}
                {ticket.created_by ? ` by ${ticket.created_by.name}` : ''}
              </span>
            </span>
          ) : (
            'Loading…'
          )
        }
        actions={
          ticket ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={copyLink} className="gap-1">
                <Link2 className="h-3.5 w-3.5" /> Copy link
              </Button>
              <GuardedAction reason={capabilities.reason.assign}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAssignOpen(true)}
                  disabled={!capabilities.canAssign}
                  className="gap-1"
                >
                  <UserPlus className="h-3.5 w-3.5" /> Assign
                </Button>
              </GuardedAction>
              <GuardedAction reason={capabilities.reason.escalate}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEscalateOpen(true)}
                  disabled={!capabilities.canEscalate}
                  className="gap-1 border-warning/50 text-warning-soft hover:bg-warning/10"
                >
                  <ShieldAlert className="h-3.5 w-3.5" /> Escalate
                </Button>
              </GuardedAction>
            </div>
          ) : null
        }
      />

      {ticketQuery.isLoading || !ticket ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-1">
            <Skeleton className="h-56" />
            <Skeleton className="h-64" />
            <Skeleton className="h-32" />
          </div>
          <div className="space-y-4 lg:col-span-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-72" />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Left: order context + state */}
          <div className="space-y-4 lg:col-span-1">

           {ticket.type!=='damage'? <TicketSummary ticket={ticket} />:null}
           {ticket.type!== 'damage'? <TicketStateCard ticket={ticket} capabilities={capabilities} />:<CycleTicketResolveCard ticket={ticket}/>}

            <Card>
              <CardHeader>
                <CardTitle>Assignments</CardTitle>
                <Badge tone="neutral">{ticket.assignments?.length ?? 0}</Badge>
              </CardHeader>
              <CardBody>
                {!ticket.assignments || ticket.assignments.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No one assigned yet. Assign or escalate to surface this in someone&rsquo;s
                    inbox.
                  </p>
                ) : (
                  <ul className="space-y-3 text-xs">
                    {ticket.assignments.map((a) => (
                      <li key={a.id} className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${a.is_primary ? 'bg-primary' : 'bg-muted-foreground/40'}`}
                            aria-hidden
                          />
                          <span className="font-medium">
                            {a.user?.name ?? `User #${a.user?.id}`}
                          </span>
                          {a.is_primary ? <Badge tone="primary">Primary</Badge> : null}
                          {a.user?.role ? (
                            <span className="text-muted-foreground">· {a.user.role}</span>
                          ) : null}
                          <span className="ml-auto">
                            {a.acknowledged_at ? (
                              <Badge tone="success">
                                Acknowledged {fmt.ago(a.acknowledged_at)}
                              </Badge>
                            ) : (
                              <Badge tone="warning">Not yet seen</Badge>
                            )}
                          </span>
                        </div>
                        <p className="pl-3.5 text-[11px] text-muted-foreground">
                          {a.assigned_by ? `Assigned by ${a.assigned_by.name}` : 'Assigned'}
                          {a.created_at ? ` · ${fmt.ago(a.created_at)}` : ''}
                        </p>
                        {a.note?.trim() ? (
                          <p className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px] text-foreground/90">
                            {a.note}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Right: disposition, the type-specific work, then the history */}
          <div className="space-y-4 lg:col-span-2">
        
            {ticket.type !=='damage'? <TicketDispositionCard ticket={ticket} capabilities={capabilities} />:<CycleDamageCard damagedCycles={ticket.cycle_damages}/>}

            {ticket.type === 'refund' ? <RefundEditor ticket={ticket} /> : null}
            {ticket.type === 'shrinkage' ? <ShrinkageEditor ticket={ticket} /> : null}

            <InteractionTimeline
              interactions={ticket.interactions ?? []}
              orderRemarks={orderRemarks}
              orderId={ticket.order_id}
              composer={
                <CallRecordingUpload ticket={ticket} capabilities={capabilities} embedded />
              }
            />
          </div>
        </div>
      )}

      {ticket ? (
        <>
          <AssignmentDialog
            ticket={ticket}
            open={assignOpen}
            onOpenChange={setAssignOpen}
            mode="assign"
          />
          <AssignmentDialog
            ticket={ticket}
            open={escalateOpen}
            onOpenChange={setEscalateOpen}
            mode="escalate"
          />
        </>
      ) : null}
    </>
  );
}
